import { z } from "zod";
/**
 * The CALIBRATION document and calibration-target templates.
 *
 * A node's CALIBRATION document is a JSON array with three entry forms. The
 * derived form calibrates a signal the node computes itself from a raw input
 * (`derived` on a device); the device form mirrors a calibration a module
 * stores in its own firmware (a DASGIP channel), so the node can re-verify it
 * after a reconnect; the pump form gives a pump output signal its flow,
 * k·speed + b in g/h, which the controllers that drive or read that pump
 * convert with. The node treats an apply as a full replacement: an entry
 * missing from the new document is gone.
 *
 * A calibration target is how a rig type calibrates one probe: the routine to
 * run and its params document written in the model's alias vocabulary. At
 * launch BrewBase substitutes the tank's exposed signals for the aliases, so
 * one template serves every tank of the model.
 */
const finite = z.number().finite();
export const derivedCalibrationEntrySchema = z.union([
    z
        .object({ device: z.string().min(1), signal: z.string().min(1), model: z.literal("linear"), a: finite, b: finite })
        .strict(),
    z
        .object({ device: z.string().min(1), signal: z.string().min(1), model: z.literal("nernst"), e0: finite, slope: finite })
        .strict(),
]);
export const channelCalibrationSchema = z
    .object({ channel: z.string().min(1), offset: finite, slope: finite })
    .strict();
export const deviceCalibrationEntrySchema = z
    .object({ device: z.string().min(1), channels: z.array(channelCalibrationSchema) })
    .strict();
export const pumpCalibrationEntrySchema = z
    .object({ device: z.string().min(1), signal: z.string().min(1), k: finite.positive(), b: finite })
    .strict();
export const calibrationEntrySchema = z.union([
    derivedCalibrationEntrySchema,
    deviceCalibrationEntrySchema,
    pumpCalibrationEntrySchema,
]);
export const calibrationDocumentSchema = z.array(calibrationEntrySchema);
export function isDeviceCalibrationEntry(entry) {
    return "channels" in entry;
}
export function isPumpCalibrationEntry(entry) {
    return "k" in entry;
}
export function isDerivedCalibrationEntry(entry) {
    return "model" in entry;
}
/** The document as the node would read it, or null when it does not parse. */
export function parseCalibrationDocument(content) {
    if (!content)
        return [];
    try {
        const result = calibrationDocumentSchema.safeParse(JSON.parse(content));
        return result.success ? result.data : null;
    }
    catch {
        return null;
    }
}
/**
 * The document with one probe's or pump's calibration replaced. A derived or
 * pump entry is keyed by device and signal; a channel by device and channel
 * name, inside the one device entry that holds all of that device's channels.
 */
export function upsertDerivedCalibration(entries, entry) {
    const rest = entries.filter((e) => !isDerivedCalibrationEntry(e) || e.device !== entry.device || e.signal !== entry.signal);
    return [...rest, entry];
}
export function upsertPumpCalibration(entries, entry) {
    const rest = entries.filter((e) => !isPumpCalibrationEntry(e) || e.device !== entry.device || e.signal !== entry.signal);
    return [...rest, entry];
}
export function upsertChannelCalibration(entries, device, channel) {
    const existing = entries.find((e) => isDeviceCalibrationEntry(e) && e.device === device);
    const channels = [...(existing?.channels ?? []).filter((c) => c.channel !== channel.channel), channel].sort((a, b) => a.channel.localeCompare(b.channel));
    const rest = entries.filter((e) => e !== existing);
    return [...rest, { device, channels }];
}
function canonical(value) {
    if (Array.isArray(value))
        return value.map(canonical);
    if (typeof value === "object" && value !== null)
        return Object.fromEntries(Object.entries(value)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, v]) => [k, canonical(v)]));
    return value;
}
/** Whether two stored calibration entries hold the same values, whatever their key order. */
export function sameCalibrationEntry(a, b) {
    return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
}
/** The derived-signal declarations a devices document carries. */
export const signalRefSchema = z
    .object({ device: z.string().nullable().optional(), signal: z.string().min(1) })
    .strict();
export const derivedSignalSchema = z.union([
    z.object({ signal: z.string().min(1), unit: z.string(), model: z.literal("linear"), input: signalRefSchema }).strict(),
    z
        .object({
        signal: z.string().min(1),
        unit: z.string(),
        model: z.literal("nernst"),
        mv: signalRefSchema,
        temp: signalRefSchema.nullable().optional(),
    })
        .strict(),
]);
/** Every `derived` entry of a devices document, with its owning device. Skips entries that don't parse. */
export function derivedSignalsOf(devicesContent) {
    if (!devicesContent)
        return [];
    let parsed;
    try {
        parsed = JSON.parse(devicesContent);
    }
    catch {
        return [];
    }
    if (!Array.isArray(parsed))
        return [];
    const out = [];
    for (const device of parsed) {
        if (typeof device !== "object" || device === null)
            continue;
        const d = device;
        if (typeof d.id !== "string" || !Array.isArray(d.derived))
            continue;
        for (const raw of d.derived) {
            const result = derivedSignalSchema.safeParse(raw);
            if (result.success)
                out.push({ device: d.id, derived: result.data });
        }
    }
    return out;
}
/** The raw inputs a derived signal reads, each resolved to its owning device. */
export function derivedInputs(entry) {
    const at = (role, ref) => ref ? [{ role, device: ref.device ?? entry.device, signal: ref.signal }] : [];
    return entry.derived.model === "linear"
        ? at("input", entry.derived.input)
        : [...at("mv", entry.derived.mv), ...at("temp", entry.derived.temp)];
}
/** `do3.do` → 3; `mx3.flow_sp` → 3. Null when the signal carries no module channel. */
export function channelOfSignal(signal) {
    const match = /^[a-z_]*?(\d+)\./i.exec(signal);
    return match ? Number(match[1]) : null;
}
function isAliasRef(value) {
    return (typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        typeof value.alias === "string" &&
        Object.keys(value).length === 1);
}
/**
 * Substitute a tank's signals for the aliases in a template.
 *
 * Two placements are understood. `{ "alias": "x" }` anywhere a value goes
 * becomes `{ "device", "signal" }` — the routine's SignalRef. A top-level
 * `"target": { "alias": "x" }` is for routines that address a module directly:
 * it becomes top-level `device` and `channel`, the channel read from the
 * exposed signal's name. Every other value passes through unchanged.
 */
export function resolveCalibrationTemplate(template, exposures) {
    const byAlias = new Map(exposures.map((e) => [e.alias, e]));
    const plumbing = [];
    const missing = [];
    const unparsed = [];
    const visit = (value, path) => {
        if (isAliasRef(value)) {
            const hit = byAlias.get(value.alias);
            if (!hit) {
                missing.push({ param: path, alias: value.alias });
                return value;
            }
            plumbing.push({ param: path, alias: value.alias, device: hit.device, signal: hit.signal });
            return { device: hit.device, signal: hit.signal };
        }
        if (Array.isArray(value))
            return value.map((item, i) => visit(item, `${path}[${i}]`));
        if (typeof value === "object" && value !== null) {
            return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, visit(item, path ? `${path}.${key}` : key)]));
        }
        return value;
    };
    const { target, ...rest } = template;
    const params = visit(rest, "");
    if (isAliasRef(target)) {
        const hit = byAlias.get(target.alias);
        if (!hit)
            missing.push({ param: "target", alias: target.alias });
        else {
            const channel = channelOfSignal(hit.signal);
            if (channel === null)
                unparsed.push({ param: "target", alias: target.alias, signal: hit.signal });
            else {
                params.device = hit.device;
                params.channel = channel;
                plumbing.push({ param: "target", alias: target.alias, device: hit.device, signal: hit.signal });
            }
        }
    }
    else if (target !== undefined) {
        params.target = target;
    }
    return { params, plumbing, missing, unparsed };
}
/** Only the keys a target allows an operator to change survive; the template wins for the rest. */
export function mergeEditableParams(resolved, overrides, editableKeys) {
    const out = { ...resolved };
    for (const key of editableKeys) {
        if (key in overrides) {
            if (overrides[key] === undefined)
                delete out[key];
            else
                out[key] = overrides[key];
        }
    }
    return out;
}
/**
 * Every SignalRef-typed property of a params schema, at the top level, so a
 * skeleton template can put `{alias}` placeholders where they go. A property
 * counts when it (or the non-null branch of a nullable union) references
 * `#/$defs/SignalRef`.
 */
export function signalRefParams(paramsSchema) {
    const properties = paramsSchema.properties;
    if (typeof properties !== "object" || properties === null)
        return [];
    const isRef = (schema) => {
        if (typeof schema !== "object" || schema === null)
            return false;
        const s = schema;
        if (s.$ref === "#/$defs/SignalRef")
            return true;
        const branches = Array.isArray(s.anyOf) ? s.anyOf : Array.isArray(s.oneOf) ? s.oneOf : [];
        return branches.some(isRef);
    };
    return Object.entries(properties)
        .filter(([, schema]) => isRef(schema))
        .map(([key]) => key);
}
/** Whether a params schema addresses a module by `device` + `channel` rather than a SignalRef. */
export function addressesChannel(paramsSchema) {
    const properties = paramsSchema.properties;
    return (typeof properties === "object" &&
        properties !== null &&
        "device" in properties &&
        "channel" in properties);
}
//# sourceMappingURL=brewcontrol-calibration.js.map